import React, { useMemo, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html as htmlLang } from '@codemirror/lang-html';
import { FileUp, AlertTriangle, CheckCircle2, Copy, ChevronDown, Wand2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { validateSimHtml } from '../../lib/simFrame';
import { CHATGPT_GUIDE } from './chatgptGuide';

export const SimCodeEditor = ({ value, onChange, onAnalyze, analyzing, report }) => {
  const [guide, setGuide] = useState(false);
  const issues = useMemo(() => validateSimHtml(value), [value]);
  const ext = useMemo(() => [htmlLang()], []);
  const loadFile = (file) => {
    if (!file) return;
    if (file.size > 3_000_000) { toast.error('حجم الملف أكبر من 3MB'); return; }
    file.text().then(onChange);
  };
  const copy = () => navigator.clipboard?.writeText(CHATGPT_GUIDE).then(() => toast.success('تم نسخ التعليمات'));
  return (
    <div className="space-y-3 min-w-0" data-testid="sim-code-editor">
      <h4 className="font-black text-slate-900">الصق كود HTML للمحاكاة</h4>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-blue-300 text-blue-600 text-sm font-semibold cursor-pointer hover:bg-blue-50">
          <FileUp className="w-4 h-4" />رفع ملف HTML
          <input data-testid="sim-code-file" type="file" accept=".html,.htm,text/html" hidden onChange={(e) => loadFile(e.target.files?.[0])} />
        </label>
        <button type="button" data-testid="sim-guide-toggle" onClick={() => setGuide(!guide)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold">
          تعليمات لـ ChatGPT<ChevronDown className={`w-4 h-4 transition-transform ${guide ? 'rotate-180' : ''}`} />
        </button>
        <span className="ms-auto text-xs text-slate-400" dir="ltr">{(value || '').length.toLocaleString('en-US')} chars</span>
      </div>
      {guide && (
        <div data-testid="sim-guide" className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-slate-800">انسخ هذا النص وأرسله لـ ChatGPT مع وصف المحاكاة التي تريدها</p>
            <button type="button" data-testid="sim-guide-copy" onClick={copy} className="inline-flex items-center gap-1 text-blue-700 text-sm font-bold"><Copy className="w-4 h-4" />نسخ</button>
          </div>
          <pre dir="ltr" className="text-[11px] leading-5 whitespace-pre-wrap max-h-60 overflow-auto text-left text-slate-700">{CHATGPT_GUIDE}</pre>
        </div>
      )}
      <div dir="ltr" className="rounded-xl overflow-hidden border border-slate-200 text-left min-w-0 max-w-full" data-testid="sim-code-input">
        <CodeMirror value={value} height="420px" extensions={ext} onChange={onChange} basicSetup={{ lineNumbers: true, foldGutter: true }}
          placeholder="<!doctype html> ... </html>" />
      </div>
      <div data-testid="sim-code-validation" className={`rounded-xl px-3 py-2 text-sm ${issues.length ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
        {issues.length ? issues.map(i => <p key={i} className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" />{i}</p>)
          : <p className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />الكود جاهز. افتح تبويب «معاينة» للتجربة.</p>}
      </div>
      <button type="button" data-testid="sim-analyze-btn" disabled={analyzing || !(value || '').trim()} onClick={onAnalyze}
        className="w-full sm:w-auto h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {analyzing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}تحليل HTML وتعبئة البيانات تلقائياً
      </button>
      {report && <AnalyzeReport report={report} />}
    </div>
  );
};

const AnalyzeReport = ({ report }) => (
  <div data-testid="sim-analyze-report" className={`rounded-xl border p-3 text-sm space-y-2 ${report.source === 'structured' ? 'border-emerald-200 bg-emerald-50/60' : report.source === 'error' ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`}>
    <p className="font-black text-slate-900">
      {report.source === 'structured' ? 'تمت قراءة البيانات المنظمة (am-simulation-data)' : report.source === 'error' ? 'تعذّرت قراءة البيانات المنظمة' : 'لم توجد بيانات منظمة'}
    </p>
    {report.filled?.length > 0 && <p data-testid="sim-analyze-filled" className="text-emerald-800">عُبّئ: {report.filled.join('، ')}</p>}
    {report.warnings?.length > 0 && (
      <ul data-testid="sim-analyze-warnings" className="space-y-1 text-amber-800">
        {report.warnings.map(w => <li key={w} className="flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{w}</li>)}
      </ul>
    )}
    {report.source !== 'error' && <p className="text-slate-500 text-xs">راجع الحقول في التبويبات الأخرى ثم احفظ كمسودة أو انشر. لن يُنشر شيء تلقائياً.</p>}
  </div>
);
